import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { Check, Copy, Loader2 } from 'lucide-react';
import {
  getDemoTeamDefault,
  setDemoTeamDefault,
  updateDemoEnvironment,
} from '../../services/api';
import type {
  DemoEnvironmentResponse,
  DemoTeamMember,
  DemoTeamRole,
  KinnserPullSummary,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { demoContentError } from './demoContent';
import { DemoContentTab } from './DemoContentTab';
import { DemoFormSection } from './DemoFormSection';
import { DemoTeamRows } from './DemoTeamRows';
import { KinnserSourcesSection } from './KinnserSourcesSection';
import { DEFAULT_DEMO_FF_TEMPLATE_AGENCY } from './demoAgencySetupDefaults';

/** A Select cannot hold an empty value, so "copy nothing" needs a name. */
const NO_FLAG_SOURCE = '__none__';
import { inferDemoAgencySetupStageLabel } from './demoAgencySetupUi';
import {
  DEMO_REFRESH_TIME_LABEL,
  resolveSettings,
  type ResolvedDemoEnvironmentSettings,
} from './demoEnvironmentStatus';

/**
 * Edit one agency's saved sandbox settings, in the pane beside the list.
 *
 * There is no add mode: every agency is already listed, and saving an agency's
 * settings for the first time is what puts it in "My sandboxes".
 *
 * An untouched agency arrives with the backend's default settings, which list
 * the GTM team as Admins. The team needs an Admin, so someone can administer
 * the sandbox. The chart seeds give visits to Admins and Field clinicians.
 *
 * One frame rather than tabs. Kinnser's part of a sandbox is one line and one
 * button -- whether the build has data to draw on -- and everything else is
 * what to seed and who gets in. Splitting three short sections across tabs hid
 * two of them behind a click for no gain. Correcting Kinnser's data is not here
 * at all: a sandbox's data is invented, so there is nothing to correct.
 *
 * The counts the source boxes read come from this sandbox's own pull, not the
 * shared Kinnser sync record. That record's API is behind a feature flag, and
 * this screen is not, so reading it would leave everyone without the flag
 * looking at four boxes that say nothing.
 */

interface Props {
  /** The sandbox being edited. */
  environment: DemoEnvironmentResponse;
  /** Every agency a sandbox could take its feature flags from. */
  agencies: readonly DemoEnvironmentResponse[];
  /** This sandbox's last pull: a fresh one when the header just pulled. */
  pull: KinnserPullSummary | null;
  /** True while the header's pull is running; a save then would race it. */
  pulling?: boolean;
  onSaved: () => void;
  /**
   * Whether a save is running, reported up so the pane can hold the pull shut.
   * A pull mid-save would change what the settings were saved against.
   */
  onSavingChange?: (saving: boolean) => void;
  /**
   * Whether an edit has been made that the server has not got yet -- the
   * autosave timer is still counting, or its request is in flight.
   *
   * Reported up so the pane can hold Run setup shut. Without it, clicking Run
   * inside the autosave window starts a build from the settings the operator
   * has just replaced, and nothing on screen says the change was dropped.
   */
  onPendingChange?: (pending: boolean) => void;
}

// The invite step fails several minutes into a run, so a typo caught here saves
// the whole round trip.
//
// The server refuses the same shape, in DEMO_TEAM_EMAIL_PATTERN in
// demo_agency_setup/defaults.py -- that one is the rule, and every way of
// saving a team goes through it. This copy only exists to mark the row before
// the request is made, so keep the two in step.
const DEMO_TEAM_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEMO_TEAM_ROLES: { value: DemoTeamRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'FIELD_CLINICIAN', label: 'Field clinician' },
];

/** The team as the save sends it: blank rows dropped, as the server does. */
const savedTeam = (team: DemoTeamMember[]): DemoTeamMember[] =>
  team
    .map(member => ({ ...member, email: member.email.trim() }))
    .filter(member => member.email);

/**
 * Where the people in these two lists sign in.
 *
 * A sandbox lives in whatever stage this console is open in, so the link is
 * this app's own origin -- which is what both headings used to say in words.
 * Nobody is emailed, so somebody has to send it to them.
 */
const signInUrl = () => `${window.location.origin}/login`;

const CopySignInLink: React.FC = () => {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const clearing = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(clearing.current), []);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(signInUrl());
      setState('copied');
    } catch {
      // Refused without a secure context, or by the browser's permissions.
      // The title carries the link, so there is still a way to get it.
      setState('failed');
    }
    clearTimeout(clearing.current);
    clearing.current = setTimeout(() => setState('idle'), 2000);
  };
  return (
    <Button
      type='button'
      variant='outline'
      size='sm'
      title={signInUrl()}
      onClick={() => void copy()}
    >
      {state === 'copied' ? (
        <Check className='mr-1 h-3.5 w-3.5' />
      ) : (
        <Copy className='mr-1 h-3.5 w-3.5' />
      )}
      {state === 'copied'
        ? 'Copied'
        : state === 'failed'
          ? 'Copy failed'
          : 'Copy sign in link'}
    </Button>
  );
};

/**
 * The rows to show, which is never none.
 *
 * An empty list rendered as a heading and an "Add person" button, so naming the
 * first person cost a click that told you nothing. A blank row is free: it is
 * what `savedTeam` drops, so it reaches no save and reads as no change.
 */
const withRowToFill = (team: DemoTeamMember[]): DemoTeamMember[] =>
  team.length > 0 ? team : [{ email: '', role: 'FIELD_CLINICIAN' }];

/**
 * The form's contents as one comparable string.
 *
 * Exactly what a save would send, so a change the save would not carry -- a
 * blank row added and removed, whitespace around an email -- does not read as
 * one.
 */
const formShape = (
  settings: ResolvedDemoEnvironmentSettings,
  team: DemoTeamMember[],
  agencyTeam: DemoTeamMember[]
): string =>
  JSON.stringify({
    ...settings,
    demo_team: savedTeam(team),
    agency_team: savedTeam(agencyTeam),
  });

export const DemoSettingsPanel: React.FC<Props> = ({
  environment,
  agencies,
  pull,
  pulling = false,
  onSaved,
  onSavingChange,
  onPendingChange,
}) => {
  const agencyId = environment.agency_id;
  // Every agency but this one: copying a sandbox's flags from itself is a
  // choice that does nothing, and offering it only invites the click.
  const flagSources = useMemo(
    () =>
      agencies
        .map(each => each.agency_system_name)
        .filter(name => name !== environment.agency_system_name)
        .sort((a, b) => a.localeCompare(b)),
    [agencies, environment.agency_system_name]
  );
  const refreshScheduled = inferDemoAgencySetupStageLabel() === 'demo';
  const [settings, setSettings] = useState<ResolvedDemoEnvironmentSettings>(
    resolveSettings(undefined, DEFAULT_DEMO_FF_TEMPLATE_AGENCY)
  );
  // The saved name when this stage has no such agency -- the default is
  // NewDay, and NewDay's own sandbox excludes itself, so the commonest box on
  // the screen would otherwise be the empty one. An unmatched value draws
  // nothing at all in the trigger, which reads as "no source" beside a
  // sentence naming one.
  const missingFlagSource =
    settings.demo_ff_template_agency &&
    !flagSources.includes(settings.demo_ff_template_agency)
      ? settings.demo_ff_template_agency
      : null;
  const [team, setTeam] = useState<DemoTeamMember[]>([]);
  // Kept out of `settings` for the same reason it is a second column: the
  // saved default is built from the team on the left, and the agency's own
  // people must not reach it.
  const [agencyTeam, setAgencyTeam] = useState<DemoTeamMember[]>([]);
  // The team a sandbox that never named one starts with. Held so Update
  // defaults can tell whether the form still matches it, and so Restore has
  // something to put back. Null until it loads.
  const [teamDefault, setTeamDefault] = useState<DemoTeamMember[] | null>(null);
  // What the form held when it loaded, so the timer below can tell whether
  // anything actually changed. A string rather than the object: the comparison
  // is deep and this block is small.
  const [baseline, setBaseline] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keyed on the agency rather than the demo config: moving to another agency
  // must clear the last one's form, and a list poll that hands back an equal
  // config must not wipe an edit.
  useEffect(() => {
    if (!agencyId) return;
    setError(null);
    const resolved = resolveSettings(
      environment.settings,
      DEFAULT_DEMO_FF_TEMPLATE_AGENCY
    );
    setSettings(resolved);
    // The rows are what save() reads, so they must hold the saved team. A
    // sandbox that never named one is seeded from the stored default instead,
    // once it arrives -- see the effect below.
    setTeam(resolved.demo_team);
    setAgencyTeam(withRowToFill(resolved.agency_team));
    setBaseline(formShape(resolved, resolved.demo_team, resolved.agency_team));
  }, [agencyId]);

  // Read once per agency: it is one small row and nothing else writes it.
  useEffect(() => {
    if (!agencyId) return;
    let cancelled = false;
    getDemoTeamDefault({ throwOnError: true })
      .then(res => {
        if (!cancelled) setTeamDefault(res.data?.team ?? []);
      })
      .catch(caught => {
        // The panel still works: the buttons simply stay disabled.
        console.error('Failed to load the demo team default', caught);
      });
    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  // A sandbox that never named a team shows the default, so the rows say what
  // a run would actually invite. The backend resolves an empty saved team the
  // same way, so this decides nothing -- it keeps the screen from showing an
  // empty team for a run that will not have one.
  //
  // Baseline moves with it, and only while it still describes the empty team:
  // arriving at the default is not an edit, but an edit made in between is.
  useEffect(() => {
    if (teamDefault === null || teamDefault.length === 0) return;
    setTeam(current => (current.length ? current : teamDefault));
    setBaseline(previous =>
      previous === formShape(settings, [], agencyTeam)
        ? formShape(settings, teamDefault, agencyTeam)
        : previous
    );
  }, [teamDefault]);

  const pulledClinics = pull?.clinic_list ?? [];
  const excluded = new Set(settings.excluded_clinic_ids);
  const setExcluded = (clinicId: string, leaveOut: boolean) =>
    setSettings(current => {
      const next = new Set(current.excluded_clinic_ids);
      if (leaveOut) next.add(clinicId);
      else next.delete(clinicId);
      return { ...current, excluded_clinic_ids: [...next] };
    });
  // Excluding every clinic at once is more likely a slip than a decision, and
  // the build refuses it, so the form says so rather than saving it.
  const allClinicsExcluded =
    pulledClinics.length > 0 &&
    settings.content.kinnser_sources.clinics &&
    pulledClinics.every(clinic => excluded.has(clinic.clinic_id));

  const contentError = demoContentError(settings.content);
  const setContent = useCallback(
    (
      content: ResolvedDemoEnvironmentSettings['content'],
      includeCoding: boolean
    ) =>
      setSettings(current => ({
        ...current,
        content,
        include_coding: includeCoding,
      })),
    []
  );

  const members = savedTeam(team);
  const invalidEmails = members
    .map(member => member.email)
    .filter(email => !DEMO_TEAM_EMAIL_PATTERN.test(email));
  // Each person gets one sandbox role; the server refuses an email listed twice.
  const seenEmails = new Set<string>();
  const repeatedEmails = new Set<string>();
  for (const { email } of members) {
    const key = email.toLowerCase();
    if (seenEmails.has(key)) repeatedEmails.add(key);
    seenEmails.add(key);
  }
  const hasAdmin = members.some(member => member.role === 'ADMIN');

  // Compared as saved, so whitespace and case do not read as a change.
  const sameTeam = (a: DemoTeamMember[], b: DemoTeamMember[]) =>
    a.length === b.length &&
    a.every(
      (member, index) =>
        member.email.trim().toLowerCase() ===
          b[index].email.trim().toLowerCase() && member.role === b[index].role
    );
  const teamIsDefault = teamDefault !== null && sameTeam(members, teamDefault);
  const teamIsValid =
    invalidEmails.length === 0 && repeatedEmails.size === 0 && hasAdmin;

  const saveDefault = async () => {
    setSavingDefault(true);
    setError(null);
    try {
      const res = await setDemoTeamDefault({
        body: { team: members },
        throwOnError: true,
      });
      // Held, so the button disables itself without another read.
      setTeamDefault(res.data?.team ?? members);
    } catch (caught) {
      console.error('Failed to save the demo team default', caught);
      setError(
        getBusinessErrorMessage(caught) ??
          'Unable to save the default team. Please try again.'
      );
    } finally {
      setSavingDefault(false);
    }
  };

  // No Admin rule here: the Roger team on the left already carries one, and an
  // agency may be shown a sandbox without any of its own people in it.
  const agencyMembers = savedTeam(agencyTeam);
  const invalidAgencyEmails = agencyMembers
    .map(member => member.email)
    .filter(email => !DEMO_TEAM_EMAIL_PATTERN.test(email));
  // The server refuses a repeat in this column too, so without this the save
  // fails on every keystroke with nothing marking the row that caused it.
  const seenAgencyEmails = new Set<string>();
  const repeatedAgencyEmails = new Set<string>();
  for (const { email } of agencyMembers) {
    const key = email.toLowerCase();
    if (seenAgencyEmails.has(key)) repeatedAgencyEmails.add(key);
    seenAgencyEmails.add(key);
  }

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  // Counts the saves, so a slow one landing after a quick one cannot report its
  // outcome over the newer one's. Each save sends the whole settings block, so
  // the values themselves are last-write-wins either way.
  const saveSeq = useRef(0);

  const save = async (
    next: ResolvedDemoEnvironmentSettings,
    nextTeam: DemoTeamMember[],
    nextAgencyTeam: DemoTeamMember[]
  ) => {
    if (!agencyId) return;
    const seq = ++saveSeq.current;
    setSaving(true);
    setError(null);
    try {
      await updateDemoEnvironment({
        path: { agency_id: agencyId },
        // The server replaces the whole saved block, so start from what it
        // sent: a setting this panel does not edit keeps its saved value.
        body: {
          settings: {
            ...environment.settings,
            ...next,
            // A team that still matches the loaded default was never actually
            // chosen -- it is what the effect above filled the empty rows
            // with for display. Saving it verbatim would turn "follow the
            // default" into "pinned to today's default", so an unrelated
            // settings edit stops a later default change from reaching this
            // sandbox.
            demo_team:
              teamDefault !== null && sameTeam(savedTeam(nextTeam), teamDefault)
                ? []
                : savedTeam(nextTeam),
            agency_team: savedTeam(nextAgencyTeam),
          },
        },
        throwOnError: true,
      });
      // The list refresh does not reseed the form -- that effect is keyed on
      // the agency -- so the new baseline is set here, or the next tick would
      // see the same change again and save it twice.
      setBaseline(formShape(next, nextTeam, nextAgencyTeam));
      if (seq === saveSeq.current) onSaved();
    } catch (caught) {
      console.error('Failed to save demo settings', caught);
      if (seq === saveSeq.current) {
        setError(
          getBusinessErrorMessage(caught) ??
            'Unable to save these settings. Please try again.'
        );
      }
    } finally {
      if (seq === saveSeq.current) setSaving(false);
    }
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  /**
   * Saved as it is edited, rather than behind a Save button.
   *
   * On a timer rather than on every change, because half of this form is text:
   * saving per keystroke would be a request per letter of an email address. The
   * discrete controls -- the four source toggles, the nightly switch, the role
   * pickers -- wait the same moment, which is cheap and keeps one rule.
   *
   * A form that would be refused is not sent: a missing Admin, a repeated or
   * malformed email, a content choice the server rejects. Each says so where it
   * is, and the next valid edit saves the lot.
   */
  const unsavable =
    pulling ||
    allClinicsExcluded ||
    invalidAgencyEmails.length > 0 ||
    repeatedAgencyEmails.size > 0 ||
    invalidEmails.length > 0 ||
    repeatedEmails.size > 0 ||
    !hasAdmin ||
    contentError !== null;
  const shape = formShape(settings, team, agencyTeam);
  // An edit the server has not got yet: the timer below is still counting, a
  // save is in flight, or the form differs from what was last saved but
  // cannot be sent as-is (a missing Admin, an invalid email). That last case
  // still counts -- Run would otherwise build from the stale saved settings
  // while the screen shows something else, which is worse than holding Run
  // shut. Fixing the invalid field or reverting the edit clears it.
  const pending = shape !== baseline || saving;
  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);
  useEffect(() => {
    if (!agencyId || unsavable || shape === baseline) return;
    const timer = window.setTimeout(
      () => void saveRef.current(settings, team, agencyTeam),
      700
    );
    return () => window.clearTimeout(timer);
    // `settings` and `team` are what `shape` is made of, and a new array or
    // object of the same values must not restart the timer.
  }, [agencyId, unsavable, shape, baseline]);

  return (
    <div className='flex flex-col'>
      <div className='space-y-3'>
        {/* Which of the pull's four kinds a build draws on. It used to sit
            inside the pull line, because the line is what said whether there
            was anything to draw on; the line is in the pane header now, and
            this is a setting, so it stands on its own. */}
        <KinnserSourcesSection
          agencyId={environment.agency_id}
          sources={settings.content.kinnser_sources}
          pull={pull}
          excluded={excluded}
          onExcludedChange={setExcluded}
          identities={settings.clinic_identity}
          onIdentitiesChange={clinicIdentity =>
            setSettings(current => ({
              ...current,
              clinic_identity: clinicIdentity,
            }))
          }
          onChange={sources =>
            setContent(
              { ...settings.content, kinnser_sources: sources },
              settings.include_coding
            )
          }
        />

        <DemoContentTab
          content={settings.content}
          includeCoding={settings.include_coding}
          onChange={setContent}
          // Both are things a run does rather than data it seeds, so they
          // share one box; feature flags alone never filled its own. It is
          // handed to the content tab so it pairs with Coding in that grid,
          // rather than starting a row of its own below it.
          trailing={[
            <DemoFormSection key='run-options' title='Run options'>
              <div className='space-y-2'>
                {/* Off and disabled where the nightly job does not run. Only
                    the display changes: the saved value is still sent back on
                    save. */}
                <label className='flex items-center gap-2'>
                  <Checkbox
                    checked={refreshScheduled && settings.refresh_nightly}
                    disabled={!refreshScheduled}
                    onCheckedChange={checked =>
                      setSettings(current => ({
                        ...current,
                        refresh_nightly: checked === true,
                      }))
                    }
                  />
                  <span
                    className={`text-xs ${refreshScheduled ? '' : 'text-muted-foreground'}`}
                  >
                    Refresh nightly at {DEMO_REFRESH_TIME_LABEL}
                    {!refreshScheduled && ' (demo environment only)'}
                  </span>
                </label>
              </div>
            </DemoFormSection>,
            /* Its own box rather than a line in Run options: which agency a
               sandbox takes its flags from decides what the demo can even
               show, and that is not a run option like a nightly job.

               A list of the agencies there are, not a name to type. The field
               took any string, which invited a guess at an agency that does
               not exist and a build that quietly copied nothing. */
            <DemoFormSection key='feature-flags' title='Feature flags'>
              <div className='space-y-2'>
                <Select
                  value={settings.demo_ff_template_agency || NO_FLAG_SOURCE}
                  onValueChange={value =>
                    setSettings(current => ({
                      ...current,
                      demo_ff_template_agency:
                        value === NO_FLAG_SOURCE ? '' : value,
                    }))
                  }
                >
                  <SelectTrigger selectSize='sm' aria-label='Copy flags from'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_FLAG_SOURCE}>
                      Leave this sandbox's flags alone
                    </SelectItem>
                    {missingFlagSource && (
                      <SelectItem value={missingFlagSource}>
                        Copy from {missingFlagSource} — not on this stage
                      </SelectItem>
                    )}
                    {flagSources.map(name => (
                      <SelectItem key={name} value={name}>
                        Copy from {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                  {settings.demo_ff_template_agency
                    ? `Every flag this sandbox has is set to whatever ${settings.demo_ff_template_agency} has.`
                    : 'A build changes no flags, so the sandbox keeps whatever it already has.'}
                </p>
              </div>
            </DemoFormSection>,
          ]}
        />
        {contentError && <p className='text-sm text-red-700'>{contentError}</p>}

        {/* One box, two columns: the Roger team on the left, the agency's own
            people on the right. They were two boxes, which gave the same sign
            in link two buttons and read as two links -- and the lists differ in
            one way only, which a pair of columns shows better than a pair of
            headings did.

            That difference is the default. The Roger team is the same handful
            every time, so it has a saved one and the buttons to manage it. The
            agency's people are chosen per sandbox, so they have none, and
            nothing on the right writes to one.

            "Access" rather than "invites": a build creates the account where
            WorkOS has none and makes the membership active, and emails nobody.
            Nobody here is being asked whether they would like to join a sandbox
            somebody has already decided they are in. */}
        <DemoFormSection title='Access' actions={<CopySignInLink />}>
          <div className='grid gap-x-6 gap-y-5 md:grid-cols-2'>
            <div className='space-y-2'>
              <h4 className='text-xs font-medium'>Roger team</h4>
              <DemoTeamRows
                members={team}
                roles={DEMO_TEAM_ROLES}
                label='Team'
                placeholder='someone@rogerhealthcare.com'
                onChange={setTeam}
              />

              {/* What is wrong, and the actions, on a line together. */}
              <div className='flex flex-wrap items-center justify-between gap-2 pt-1'>
                <div className='space-y-0.5'>
                  {invalidEmails.length > 0 && (
                    <p className='text-xs text-red-700'>
                      Not a valid email: {invalidEmails.join(', ')}
                    </p>
                  )}
                  {repeatedEmails.size > 0 && (
                    <p className='text-xs text-red-700'>
                      Listed more than once: {[...repeatedEmails].join(', ')}
                    </p>
                  )}
                  {!hasAdmin && (
                    <p className='text-xs text-red-700'>
                      Add at least one Admin
                    </p>
                  )}
                </div>
                <div className='ml-auto flex flex-wrap items-center gap-2'>
                  {/* Both are about the default rather than this sandbox, so
                      they sit together and away from Add person. Both are
                      disabled for the same reason: there is nothing to do when
                      the form already is the default. */}
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    disabled={teamDefault === null || teamIsDefault}
                    onClick={() => setTeam(teamDefault ?? [])}
                  >
                    Restore default
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    disabled={
                      teamDefault === null ||
                      teamIsDefault ||
                      !teamIsValid ||
                      savingDefault
                    }
                    onClick={() => void saveDefault()}
                  >
                    {savingDefault && (
                      <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                    )}
                    Update defaults
                  </Button>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() =>
                      setTeam(current => [
                        ...current,
                        { email: '', role: 'ADMIN' },
                      ])
                    }
                  >
                    Add person
                  </Button>
                </div>
              </div>
            </div>

            <div className='space-y-2'>
              <h4 className='text-xs font-medium'>The agency</h4>
              <DemoTeamRows
                members={agencyTeam}
                roles={DEMO_TEAM_ROLES}
                label='Agency person'
                placeholder='someone@theagency.com'
                onChange={setAgencyTeam}
              />
              <div className='flex flex-wrap items-center justify-between gap-2 pt-1'>
                <div className='space-y-0.5'>
                  {repeatedAgencyEmails.size > 0 && (
                    <p className='text-xs text-red-700'>
                      Listed more than once:{' '}
                      {[...repeatedAgencyEmails].join(', ')}
                    </p>
                  )}
                  {invalidAgencyEmails.length > 0 && (
                    <p className='text-xs text-red-700'>
                      Not a valid email: {invalidAgencyEmails.join(', ')}
                    </p>
                  )}
                </div>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  className='ml-auto'
                  onClick={() =>
                    setAgencyTeam(current => [
                      ...current,
                      { email: '', role: 'FIELD_CLINICIAN' },
                    ])
                  }
                >
                  Add person
                </Button>
              </div>
            </div>
          </div>
        </DemoFormSection>

        {/* Stays with the form even though the actions are in the header: a
            message about what went wrong belongs beside what it went wrong
            on. */}
        {error && <p className='text-sm text-red-700'>{error}</p>}
      </div>
    </div>
  );
};

export default DemoSettingsPanel;
